import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiDownload, FiSend, FiFileText, FiCheckCircle, FiClock, FiMail, FiXCircle } from 'react-icons/fi';
import api from '../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'signed', label: 'Signed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_ICONS = {
  draft: FiFileText,
  sent: FiMail,
  viewed: FiEye,
  signed: FiCheckCircle,
  expired: FiClock,
  cancelled: FiXCircle,
};

const STATUS_STYLES = {
  draft: { background: '#f3f4f6', color: '#374151' },
  sent: { background: '#dbeafe', color: '#1e40af' },
  viewed: { background: '#fef3c7', color: '#92400e' },
  signed: { background: '#dcfce7', color: '#166534' },
  expired: { background: '#fee2e2', color: '#dc2626' },
  cancelled: { background: '#f3f4f6', color: '#6b7280' },
};

export default function Contracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', 20);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      
      const response = await api.get('/contracts/?' + params.toString());
      setContracts(response.data.contracts || []);
      setTotalPages(response.data.pages || 1);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      setError('Failed to load contracts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, status, search]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    
    try {
      await api.delete('/contracts/' + id);
      setContracts(contracts.filter(c => c.id !== id));
    } catch (err) {
      alert('Failed to delete contract');
      console.error(err);
    }
  };

  const handleSend = async (id) => {
    if (!window.confirm('Send this contract to the customer?')) return;
    
    try {
      await api.post('/contracts/' + id + '/send');
      fetchContracts();
    } catch (err) {
      alert('Failed to send contract');
      console.error(err);
    }
  };

  const handleDownload = async (id, format = 'docx') => {
    try {
      const response = await api.get('/contracts/' + id + '/download?format=' + format, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract.' + format);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download contract');
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getStatusIcon = (statusVal) => {
    const Icon = STATUS_ICONS[statusVal] || FiFileText;
    return <Icon size={14} />;
  };

  const getStatusStyle = (statusVal) => {
    return STATUS_STYLES[statusVal] || STATUS_STYLES.draft;
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Contracts</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FiFileText /> Contracts
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage contracts and agreements
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/contracts/new')}>
            <FiPlus /> Generate Contract
          </button>
        </div>

        {/* Filter Bar */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search contracts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '250px'
            }}
          />
          <select 
            value={status} 
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="form-select"
            style={{ maxWidth: '150px' }}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem'
          }}>
            <div className="spinner"></div>
          </div>
        ) : error ? (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        ) : contracts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            background: 'var(--gray-50)',
            borderRadius: '12px',
            border: '2px dashed var(--gray-200)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'var(--gray-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'var(--gray-500)'
            }}>
              <FiFileText />
            </div>
            <h3 style={{ color: 'var(--gray-700)', marginBottom: '0.5rem' }}>No Contracts Found</h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Generate your first contract from a template.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/contracts/new')}>
              <FiPlus /> Generate Contract
            </button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Contract #</th>
                    <th>Title</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map(contract => (
                    <tr key={contract.id}>
                      <td>
                        <span 
                          onClick={() => navigate('/contracts/' + contract.id)}
                          style={{ 
                            fontWeight: 600, 
                            color: 'var(--primary-600)', 
                            cursor: 'pointer' 
                          }}
                        >
                          {contract.contract_number}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 500, color: 'var(--gray-900)' }}>
                          {contract.title}
                        </span>
                      </td>
                      <td>
                        {contract.customer ? (
                          <div>
                            <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>
                              {contract.customer.name}
                            </div>
                            {contract.customer.email && (
                              <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                                {contract.customer.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>No customer</span>
                        )}
                      </td>
                      <td>
                        <span style={{ fontWeight: 600 }}>
                          {formatCurrency(contract.amount, contract.currency_code)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 500,
                          ...getStatusStyle(contract.status)
                        }}>
                          {getStatusIcon(contract.status)}
                          {contract.status}
                        </span>
                      </td>
                      <td>{formatDate(contract.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            onClick={() => navigate('/contracts/' + contract.id)}
                            className="btn btn-sm btn-secondary"
                            title="View"
                          >
                            <FiEye />
                          </button>
                          {contract.status === 'draft' && (
                            <>
                              <button
                                onClick={() => navigate('/contracts/' + contract.id + '/edit')}
                                className="btn btn-sm btn-secondary"
                                title="Edit"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                onClick={() => handleSend(contract.id)}
                                className="btn btn-sm btn-success"
                                title="Send"
                              >
                                <FiSend />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDownload(contract.id)}
                            className="btn btn-sm btn-secondary"
                            title="Download"
                          >
                            <FiDownload />
                          </button>
                          {contract.status === 'draft' && (
                            <button
                              onClick={() => handleDelete(contract.id)}
                              className="btn btn-sm btn-danger"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--gray-200)'
            }}>
              <span style={{ fontSize: '14px', color: 'var(--gray-600)' }}>
                Showing {contracts.length} of {totalCount} contracts
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
